import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Clock,
  CheckCircle,
  XCircle,
  Trophy,
  ArrowLeft,
  AlertCircle,
} from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { supabase } from "../lib/supabase";
import { useAuth } from "../context/AuthContext";

interface Question {
  number: number;
  question: string;
  answer: string;
  solution: string;
  topic: string;
  difficulty: string;
}

interface SamplePaper {
  id: string;
  title: string;
  description: string;
  level: string;
  time_limit_minutes: number;
  total_questions: number;
  questions: Question[];
}

export default function SamplePaperTest() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [paper, setPaper] = useState<SamplePaper | null>(null);
  const [loading, setLoading] = useState(true);
  const [userAnswers, setUserAnswers] = useState<Record<number, string>>({});
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [score, setScore] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [startTime] = useState(Date.now());

  useEffect(() => {
    loadPaper();
  }, [id]);

  useEffect(() => {
    if (timeRemaining <= 0 || isSubmitted) return;

    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) {
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [timeRemaining, isSubmitted]);

  const loadPaper = async () => {
    try {
      const { data, error } = await supabase
        .from("sample_papers")
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;

      setPaper(data);
      setTimeRemaining(data.time_limit_minutes * 60);

      // Initialize answers
      const initialAnswers: Record<number, string> = {};
      data.questions.forEach((q: Question) => {
        initialAnswers[q.number] = "";
      });
      setUserAnswers(initialAnswers);
    } catch (error) {
      console.error("Failed to load paper:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionNumber: number, value: string) => {
    setUserAnswers((prev) => ({
      ...prev,
      [questionNumber]: value,
    }));
  };

  const handleSubmit = async () => {
    if (!paper || !user) return;

    setSubmitting(true);

    try {
      // Calculate score
      let correctCount = 0;
      paper.questions.forEach((q) => {
        const normalizeAnswer = (s: string) => s.replace(/\s+/g, "").toLowerCase();
        const userAnswer = normalizeAnswer(userAnswers[q.number] ?? "");
        const correctAnswer = normalizeAnswer(q.answer);
        if (userAnswer === correctAnswer) {
          correctCount++;
        }
      });

      const totalTime = Math.floor((Date.now() - startTime) / 1000);
      const scoreValue = correctCount;

      // Save attempt to database
      const { error } = await supabase.from("sample_paper_attempts").insert({
        user_id: user.id,
        paper_id: paper.id,
        score: scoreValue,
        time_taken_seconds: totalTime,
        answers: userAnswers,
        completed_at: new Date().toISOString(),
      });

      if (error) throw error;

      setScore(scoreValue);
      setIsSubmitted(true);
    } catch (error) {
      console.error("Failed to submit paper:", error);
      toast({
        title: "Oops! Something went wrong 😅",
        description: "We couldn't send your answers. Please try again!",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case "easy":
        return "bg-green-100 text-green-700";
      case "medium":
        return "bg-yellow-100 text-yellow-700";
      case "hard":
        return "bg-red-100 text-red-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl">Loading paper...</div>
      </div>
    );
  }

  if (!paper) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardContent className="py-12 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <p className="text-xl text-gray-600 mb-4">Paper not found</p>
            <Button onClick={() => navigate("/sample-papers")}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Sample Papers
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isSubmitted) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Card className="max-w-4xl mx-auto">
          <CardHeader className="text-center">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Trophy className="w-10 h-10 text-blue-600" />
            </div>
            <CardTitle className="text-3xl">Test Completed!</CardTitle>
            <CardDescription>Here are your results</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Score Summary */}
            <div className="text-center p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg">
              <div className="text-5xl font-bold text-blue-600 mb-2">
                {score}/{paper.total_questions}
              </div>
              <p className="text-gray-600">
                {Math.round((score / paper.total_questions) * 100)}% Correct
              </p>
            </div>

            {/* Question Results */}
            <div className="space-y-4">
              <h3 className="text-xl font-bold">Solutions</h3>
              {paper.questions.map((question) => {
                const userAnswer = userAnswers[question.number]
                  ?.trim()
                  .toLowerCase();
                const correctAnswer = question.answer.trim().toLowerCase();
                const isCorrect = userAnswer === correctAnswer;

                return (
                  <Card
                    key={question.number}
                    className={
                      isCorrect
                        ? "border-green-200 bg-green-50"
                        : "border-red-200 bg-red-50"
                    }
                  >
                    <CardContent className="pt-6">
                      <div className="flex items-start gap-3 mb-3">
                        <div className="flex-shrink-0">
                          {isCorrect ? (
                            <CheckCircle className="w-6 h-6 text-green-600" />
                          ) : (
                            <XCircle className="w-6 h-6 text-red-600" />
                          )}
                        </div>
                        <div className="flex-grow">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="font-bold">
                              Question {question.number}
                            </span>
                            <Badge
                              className={getDifficultyColor(
                                question.difficulty,
                              )}
                            >
                              {question.difficulty}
                            </Badge>
                            <Badge variant="outline">{question.topic}</Badge>
                          </div>
                          <p className="text-gray-700 mb-3">
                            {question.question}
                          </p>

                          <div className="space-y-2 bg-white p-4 rounded border">
                            <div>
                              <span className="font-semibold">
                                Your Answer:{" "}
                              </span>
                              <span
                                className={
                                  isCorrect ? "text-green-700" : "text-red-700"
                                }
                              >
                                {userAnswers[question.number] || "(No answer)"}
                              </span>
                            </div>
                            {!isCorrect && (
                              <div>
                                <span className="font-semibold">
                                  Correct Answer:{" "}
                                </span>
                                <span className="text-green-700">
                                  {question.answer}
                                </span>
                              </div>
                            )}
                            <div className="mt-3 pt-3 border-t">
                              <p className="font-semibold mb-1">Solution:</p>
                              <p className="text-gray-700">
                                {question.solution}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            <div className="flex gap-4 pt-4">
              <Button
                onClick={() => navigate("/sample-papers")}
                className="flex-1"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Papers
              </Button>
              <Button
                onClick={() => window.location.reload()}
                variant="outline"
                className="flex-1"
              >
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header with Timer */}
      <div className="max-w-4xl mx-auto mb-6">
        <Card className="border-2 border-blue-200">
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {paper.title}
                </h1>
                <p className="text-gray-600">{paper.description}</p>
              </div>
              <div className="flex flex-col items-center gap-2">
                <Clock
                  className={`w-8 h-8 ${timeRemaining < 300 ? "text-red-600" : "text-blue-600"}`}
                />
                <div
                  className={`text-2xl font-bold ${timeRemaining < 300 ? "text-red-600" : "text-blue-600"}`}
                >
                  {formatTime(timeRemaining)}
                </div>
                <span className="text-sm text-gray-500">Time Left</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Questions */}
      <div className="max-w-4xl mx-auto space-y-6">
        {paper.questions.map((question) => (
          <Card key={question.number}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-xl">
                  Question {question.number}
                </CardTitle>
                <div className="flex gap-2">
                  <Badge className={getDifficultyColor(question.difficulty)}>
                    {question.difficulty}
                  </Badge>
                  <Badge variant="outline">{question.topic}</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-lg text-gray-700 leading-relaxed">
                {question.question}
              </p>

              <div className="space-y-2">
                <label className="text-sm font-semibold text-gray-700">
                  Your Answer:
                </label>
                <Input
                  type="text"
                  placeholder="Enter your answer..."
                  value={userAnswers[question.number] || ""}
                  onChange={(e) =>
                    handleAnswerChange(question.number, e.target.value)
                  }
                  className="text-lg"
                  disabled={submitting}
                />
              </div>
            </CardContent>
          </Card>
        ))}

        {/* Submit Button */}
        <Card className="border-2 border-blue-200 bg-blue-50">
          <CardContent className="py-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-gray-900">Ready to submit?</p>
                <p className="text-sm text-gray-600">
                  Make sure you've answered all questions before submitting.
                </p>
              </div>
              <Button
                onClick={handleSubmit}
                size="lg"
                disabled={submitting}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {submitting ? "Submitting..." : "Submit Test"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
